/**
 * Income Tax (India FY 2025-26) — employee self-service + HR admin.
 *
 * Auto-switches view based on user role:
 *   - Employee → MyDeclaration (regime picker, declarations form, live tax
 *     projection, Form 12BB proof uploads)
 *   - HR / Admin → AdminDeclarations (per-employee declaration list,
 *     verify/reject, lock, proof viewer)
 *
 * Backend: /api/income-tax/* (see income_tax_routes.py)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Calculator, FileArrowUp, CheckCircle, XCircle, LockSimple, Eye, TrashSimple, Sparkle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const HR_ROLES = ["super_admin", "company_admin", "country_head", "region_head"];

const SECTIONS = [
  { key: "HRA", label: "Rent / HRA receipts" },
  { key: "80C", label: "80C — LIC, ELSS, PPF, PF, principal HL, tuition" },
  { key: "80CCD(1B)", label: "80CCD(1B) — NPS self contribution (₹50k cap)" },
  { key: "80D", label: "80D — Health insurance" },
  { key: "80E", label: "80E — Education loan interest" },
  { key: "80G", label: "80G — Donations" },
  { key: "24(b)", label: "24(b) — Home loan interest" },
  { key: "LTA", label: "LTA — Travel exemption" },
  { key: "OTHER", label: "Other proofs" },
];

export default function IncomeTaxTab() {
  const { user } = useAuth();
  const isHR = HR_ROLES.includes(user?.role);
  return isHR ? <AdminDeclarations /> : <MyDeclaration />;
}

/* =========================================================================
   EMPLOYEE — MY DECLARATION
   ========================================================================= */
function MyDeclaration() {
  const [state, setState] = useState({ loading: true });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [showProofs, setShowProofs] = useState(false);
  const saveTimer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/income-tax/my");
      setState({ loading: false, data: r.data });
      setForm({ ...formFromDecl(r.data.declaration) });
    } catch (e) { setState({ loading: false, error: formatApiError(e) }); }
  };
  useEffect(() => { load(); }, []);

  // Auto-save with debounce when form changes (after initial load)
  useEffect(() => {
    if (!form || !state.data) return;
    if (state.data.declaration.status === "locked") return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveDraft, 800);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const saveDraft = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const r = await api.put("/income-tax/my", form);
      setState((s) => ({ ...s, data: { ...s.data, declaration: r.data.declaration } }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/income-tax/my/submit");
      toast.success("Declaration submitted to HR for verification");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };

  if (state.loading) return <div className="text-zinc-500 text-sm py-6">Loading your tax declaration…</div>;
  if (state.error) return <div className="text-rose-700 text-sm py-6">{state.error}</div>;

  const decl = state.data.declaration;
  const sal = state.data.salary;
  const computation = decl.computation || {};
  const computationAlt = decl.computation_alt || {};
  const recommended = decl.recommended_regime;
  const recommendedSavings = decl.recommended_savings || 0;
  const status = decl.status;
  const locked = status === "locked";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5" data-testid="it-my-page">
      {/* Status strip + summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatTile label="FY 2025-26 status" value={statusLabel(status)} accent={statusColor(status)} testid="it-status" />
        <StatTile label="Annual CTC" value={inr(sal.ctc_annual)} testid="it-ctc" />
        <StatTile label="Est. annual tax" value={inr(computation.total_tax)} testid="it-annual-tax" />
        <StatTile label="Monthly TDS" value={inr(computation.monthly_tds)} accent="emerald" testid="it-monthly-tds" />
      </div>

      {recommended && recommended !== form?.regime && (
        <Card className="p-3 bg-amber-50 border-amber-200 flex items-start gap-2" data-testid="it-recommend">
          <Sparkle size={18} className="text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900 flex-1">
            Based on what you&apos;ve entered, switching to the <strong>{recommended === "new" ? "New" : "Old"} regime</strong> would save you {inr(recommendedSavings)} per year.
            <button onClick={() => set("regime", recommended)} className="ml-2 underline font-semibold">Switch now</button>
          </div>
        </Card>
      )}

      <Tabs defaultValue="declarations" className="space-y-4">
        <TabsList data-testid="it-tabs">
          <TabsTrigger value="declarations" data-testid="it-tab-decl">Declarations</TabsTrigger>
          <TabsTrigger value="computation" data-testid="it-tab-comp">Tax computation</TabsTrigger>
          <TabsTrigger value="compare" data-testid="it-tab-compare">Compare regimes</TabsTrigger>
          <TabsTrigger value="proofs" data-testid="it-tab-proofs">Proofs ({(decl.proofs || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="declarations">
          <DeclarationForm form={form} set={set} sal={sal} locked={locked} />
          <div className="flex items-center justify-end gap-2 mt-3 text-xs text-zinc-500">
            {saving ? "Saving…" : status === "draft" ? "Auto-saved" : ""}
            {!locked && (
              <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 ml-2" data-testid="it-submit">
                {submitting ? "Submitting…" : status === "submitted" ? "Re-submit" : "Submit to HR"}
              </Button>
            )}
            {locked && <Badge className="bg-zinc-200 text-zinc-700">Locked by HR — no changes</Badge>}
          </div>
        </TabsContent>

        <TabsContent value="computation">
          <ComputationView c={computation} />
        </TabsContent>

        <TabsContent value="compare">
          <RegimeCompare current={computation} alt={computationAlt} recommended={recommended} regime={form?.regime} />
        </TabsContent>

        <TabsContent value="proofs">
          <ProofManager decl={decl} reload={load} locked={locked} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function formFromDecl(d) {
  return {
    regime: d.regime || "new",
    rent_paid_annual: d.rent_paid_annual || 0,
    metro_rent: d.metro_rent ?? true,
    landlord_pan: d.landlord_pan || "",
    landlord_name: d.landlord_name || "",
    deduction_80c: d.deduction_80c || 0,
    deduction_80ccd1b: d.deduction_80ccd1b || 0,
    deduction_80d_self: d.deduction_80d_self || 0,
    deduction_80d_parents: d.deduction_80d_parents || 0,
    parents_senior: d.parents_senior || false,
    deduction_80e: d.deduction_80e || 0,
    deduction_80g: d.deduction_80g || 0,
    deduction_80tta_ttb: d.deduction_80tta_ttb || 0,
    home_loan_interest: d.home_loan_interest || 0,
    home_loan_principal_in_80c: d.home_loan_principal_in_80c || false,
    home_loan_lender: d.home_loan_lender || "",
    deduction_80ccd2_employer_nps: d.deduction_80ccd2_employer_nps || 0,
    lta_exempt: d.lta_exempt || 0,
    other_exempt_perqs: d.other_exempt_perqs || 0,
    professional_tax_annual: d.professional_tax_annual || 0,
    other_taxable_income: d.other_taxable_income || 0,
  };
}

function statusLabel(s) {
  return ({ draft: "Draft", submitted: "Submitted", verified: "Verified by HR", rejected: "Needs revision", locked: "Locked" })[s] || s;
}
function statusColor(s) {
  return ({ draft: "amber", submitted: "indigo", verified: "emerald", rejected: "rose", locked: "zinc" })[s] || "zinc";
}

function StatTile({ label, value, accent = "zinc", testid }) {
  const color = {
    zinc: "border-zinc-200", amber: "border-amber-300 bg-amber-50",
    emerald: "border-emerald-300 bg-emerald-50", indigo: "border-indigo-300 bg-indigo-50",
    rose: "border-rose-300 bg-rose-50",
  }[accent] || "border-zinc-200";
  return (
    <Card className={`p-3 ${color}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="text-lg font-bold text-zinc-900 mt-0.5">{value}</div>
    </Card>
  );
}

/* ---- Declaration form ---- */
function NumField({ id, label, hint, max, form, set, locked, ...props }) {
  return (
    <div>
      <Label className="text-xs">{label} {max && <span className="text-zinc-400">(max {inr(max)})</span>}</Label>
      <Input type="number" min={0} step={1000} disabled={locked} data-testid={`it-${id}`}
        value={form[id]} onChange={(e) => set(id, Number(e.target.value) || 0)} {...props} />
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function DeclarationForm({ form, set, sal, locked }) {
  if (!form) return null;
  const isOld = form.regime === "old";

  return (
    <div className="space-y-5">
      {/* Regime picker */}
      <Card className="p-4">
        <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Tax regime</Label>
        <div className="flex gap-2 mt-2">
          {["new", "old"].map((r) => (
            <button key={r} type="button" disabled={locked} onClick={() => set("regime", r)}
              className={`px-4 py-2 rounded-md text-sm border-2 ${form.regime === r ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`it-regime-${r}`}>
              {r === "new" ? "New regime (default)" : "Old regime"}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 mt-2">
          {isOld
            ? "Old regime: allows HRA, 80C, 80D, home loan interest. Higher slabs but more deductions."
            : "New regime: lower slabs + ₹75,000 standard deduction + 87A rebate up to ₹7L NI. Most deductions disallowed."}
        </div>
      </Card>

      {/* HRA (old only) */}
      {isOld && (
        <Card className="p-4 space-y-3">
          <SectionTitle>Section 10(13A) — House Rent Allowance</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumField form={form} set={set} locked={locked} id="rent_paid_annual" label="Annual rent paid" hint={`Your HRA from CTC: ${inr(sal.hra_annual)}`} />
            <div>
              <Label className="text-xs">City type</Label>
              <div className="flex gap-2 mt-1">
                {[{ k: true, l: "Metro (50%)" }, { k: false, l: "Non-metro (40%)" }].map((o) => (
                  <button key={String(o.k)} type="button" disabled={locked} onClick={() => set("metro_rent", o.k)}
                    className={`px-3 py-1.5 rounded-md text-xs border ${form.metro_rent === o.k ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 hover:bg-zinc-50"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {form.rent_paid_annual > 100000 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Landlord PAN <span className="text-rose-600">*</span></Label>
                <Input value={form.landlord_pan} disabled={locked} onChange={(e) => set("landlord_pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="it-landlord-pan" />
                <div className="text-[10px] text-zinc-500 mt-0.5">Mandatory when annual rent &gt; ₹1,00,000</div>
              </div>
              <div>
                <Label className="text-xs">Landlord name</Label>
                <Input value={form.landlord_name} disabled={locked} onChange={(e) => set("landlord_name", e.target.value)} />
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Chapter VI-A (old only) */}
      {isOld && (
        <Card className="p-4 space-y-3">
          <SectionTitle>Chapter VI-A — Deductions</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumField form={form} set={set} locked={locked} id="deduction_80c" label="80C — Investments + PF + tuition" max={150000}
              hint="LIC premium, ELSS, PPF, EPF, principal home loan, Sukanya, tuition fees, NSC, tax-saver FD" />
            <NumField form={form} set={set} locked={locked} id="deduction_80ccd1b" label="80CCD(1B) — NPS self contribution" max={50000} hint="Over and above 80C cap" />
            <NumField form={form} set={set} locked={locked} id="deduction_80d_self" label="80D — Health (self+spouse+children)" max={25000} hint="Cap raises to ₹50k if you're 60+" />
            <NumField form={form} set={set} locked={locked} id="deduction_80d_parents" label="80D — Parents' health" max={25000} />
            <NumField form={form} set={set} locked={locked} id="deduction_80e" label="80E — Education loan interest" hint="No cap, up to 8 yrs" />
            <NumField form={form} set={set} locked={locked} id="deduction_80g" label="80G — Donations (after qualifying %)" />
            <NumField form={form} set={set} locked={locked} id="deduction_80tta_ttb" label="80TTA / 80TTB — Bank interest" max={10000}
              hint="₹10k for &lt; 60 yrs, ₹50k for 60+" />
            <NumField form={form} set={set} locked={locked} id="professional_tax_annual" label="Professional Tax paid (annual)" max={2500} />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Switch checked={form.parents_senior} onCheckedChange={(v) => set("parents_senior", !!v)} disabled={locked} data-testid="it-parents-senior" />
            <span className="text-xs text-zinc-700">Parents are senior citizens (60+) — increases 80D cap to ₹50,000</span>
          </div>
        </Card>
      )}

      {/* Home loan (old only) */}
      {isOld && (
        <Card className="p-4 space-y-3">
          <SectionTitle>Section 24(b) — Home loan interest</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumField form={form} set={set} locked={locked} id="home_loan_interest" label="Annual interest paid" max={200000} hint="Self-occupied property cap ₹2,00,000" />
            <div>
              <Label className="text-xs">Lender / Bank</Label>
              <Input value={form.home_loan_lender} disabled={locked} onChange={(e) => set("home_loan_lender", e.target.value)} />
            </div>
          </div>
        </Card>
      )}

      {/* LTA + other exempt (old only) */}
      {isOld && (
        <Card className="p-4 space-y-3">
          <SectionTitle>Exempt allowances</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumField form={form} set={set} locked={locked} id="lta_exempt" label="LTA exempt (claimed)" hint="Travel within India, 2 trips per 4-yr block" />
            <NumField form={form} set={set} locked={locked} id="other_exempt_perqs" label="Other exempt perquisites" hint="Food coupon (₹50/meal cap), telephone reimburse, etc." />
          </div>
        </Card>
      )}

      {/* New regime allowed */}
      {!isOld && (
        <Card className="p-4 space-y-3">
          <SectionTitle>Allowed under New regime</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <NumField form={form} set={set} locked={locked} id="deduction_80ccd2_employer_nps" label="80CCD(2) — Employer NPS contribution" hint="Auto-pulled from your salary structure if configured" />
          </div>
          <div className="text-xs text-zinc-500">
            Under the new regime, you get a flat ₹75,000 standard deduction (auto-applied) and a tax rebate up to ₹25,000 if your net taxable income is ≤ ₹7,00,000.
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <SectionTitle>Other income (optional)</SectionTitle>
        <NumField form={form} set={set} locked={locked} id="other_taxable_income" label="Other taxable income" hint="Freelance, interest, rental, capital gains, etc. — declared so we deduct correct TDS" />
      </Card>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{children}</div>;
}

/* ---- Computation view ---- */
function ComputationView({ c }) {
  if (!c || !c.total_tax) return <Card className="p-6 text-center text-zinc-500 text-sm">No computation yet — pick a regime and add some deductions.</Card>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-4">
        <SectionTitle>Income & deductions ({c.regime} regime)</SectionTitle>
        <Row label="Annual gross" value={inr(c.annual_gross)} />
        {c.total_exemptions > 0 && <Row label="− Exemptions (HRA + LTA + perqs)" value={`− ${inr(c.total_exemptions)}`} />}
        {c.other_taxable_income > 0 && <Row label="+ Other income" value={`+ ${inr(c.other_taxable_income)}`} />}
        <Row label="− Standard deduction" value={`− ${inr(c.standard_deduction)}`} />
        {c.professional_tax > 0 && <Row label="− Professional tax" value={`− ${inr(c.professional_tax)}`} />}
        {c.total_chapter_via_deductions > 0 && <Row label="− Chapter VI-A deductions" value={`− ${inr(c.total_chapter_via_deductions)}`} />}
        {c.home_loan_interest_deduction > 0 && <Row label="− Home loan interest u/s 24(b)" value={`− ${inr(c.home_loan_interest_deduction)}`} />}
        {c.employer_nps_80ccd2 > 0 && <Row label="− 80CCD(2) employer NPS" value={`− ${inr(c.employer_nps_80ccd2)}`} />}
        <div className="border-t border-zinc-200 mt-2 pt-2">
          <Row label="Net taxable income" value={inr(c.net_taxable_income)} bold />
        </div>
      </Card>
      <Card className="p-4">
        <SectionTitle>Tax breakdown</SectionTitle>
        <div className="space-y-1 mt-2">
          {(c.slab_breakdown || []).map((s, i) => (
            <div key={i} className="flex justify-between text-xs">
              <span className="text-zinc-600">{inr(s.from)} – {s.to === Infinity ? "∞" : inr(s.to)} @ {(s.rate * 100).toFixed(0)}%</span>
              <span className="font-medium">{inr(s.tax)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-zinc-200 mt-3 pt-2 space-y-1">
          <Row label="Tax before rebate" value={inr(c.tax_before_rebate)} />
          {c.rebate_87a > 0 && <Row label="− Sec 87A rebate" value={`− ${inr(c.rebate_87a)}`} />}
          {c.surcharge > 0 && <Row label="+ Surcharge" value={inr(c.surcharge)} />}
          <Row label="+ Health & Edu Cess @ 4%" value={inr(c.cess)} />
          <div className="border-t border-zinc-200 mt-2 pt-2 space-y-1">
            <Row label="Total annual tax" value={inr(c.total_tax)} bold />
            <Row label="Monthly TDS" value={inr(c.monthly_tds)} bold accent="emerald" />
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  const cls = `flex justify-between text-sm py-0.5 ${bold ? "font-bold" : ""} ${accent === "emerald" ? "text-emerald-700" : "text-zinc-800"}`;
  return <div className={cls}><span>{label}</span><span>{value}</span></div>;
}

/* ---- Compare regimes side-by-side ---- */
function RegimeCompare({ current, alt, recommended, regime }) {
  if (!current?.total_tax && !alt?.total_tax) return <Card className="p-6 text-center text-zinc-500 text-sm">Enter your declarations first.</Card>;
  const otherRegime = regime === "new" ? "old" : "new";
  const side = (label, c, isRec) => (
    <Card className={`p-4 ${isRec ? "border-2 border-emerald-500" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold capitalize">{label} regime</div>
        {isRec && <Badge className="bg-emerald-100 text-emerald-900">Recommended</Badge>}
      </div>
      <div className="mt-3 space-y-1">
        <Row label="Net taxable" value={inr(c.net_taxable_income)} />
        <Row label="Annual tax" value={inr(c.total_tax)} bold />
        <Row label="Monthly TDS" value={inr(c.monthly_tds)} accent="emerald" />
      </div>
    </Card>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {side(regime, current, recommended === regime)}
      {side(otherRegime, alt, recommended === otherRegime)}
    </div>
  );
}

/* ---- Proof manager ---- */
function ProofManager({ decl, reload, locked }) {
  const [section, setSection] = useState("80C");
  const [subLabel, setSubLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max file size is 5 MB"); return; }
    setBusy(true);
    try {
      const data_base64 = await fileToB64(f);
      await api.post("/income-tax/my/proofs", {
        section, sub_label: subLabel || null,
        filename: f.name, mime: f.type || "application/octet-stream",
        data_base64,
      });
      toast.success(`Uploaded ${f.name}`);
      setSubLabel("");
      if (fileInput.current) fileInput.current.value = "";
      reload();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const del = async (pid) => {
    if (!window.confirm("Remove this proof?")) return;
    try { await api.delete(`/income-tax/my/proofs/${pid}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const view = async (pid) => {
    try {
      const r = await api.get(`/income-tax/my/proofs/${pid}`);
      const { url, data_base64, mime, filename } = r.data;
      if (url) { window.open(url, "_blank"); return; }
      if (data_base64) {
        const a = document.createElement("a");
        a.href = `data:${mime};base64,${data_base64}`;
        a.download = filename || "proof";
        a.click();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <SectionTitle>Upload proof (PDF / image, max 5 MB)</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
          <div>
            <Label className="text-xs">Section</Label>
            <select className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm bg-white" value={section} onChange={(e) => setSection(e.target.value)} disabled={locked} data-testid="it-proof-section">
              {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Input value={subLabel} onChange={(e) => setSubLabel(e.target.value)} placeholder="e.g. LIC Premium Apr'25" disabled={locked} data-testid="it-proof-label" />
          </div>
          <div>
            <Label className="text-xs">File</Label>
            <input ref={fileInput} type="file" accept=".pdf,image/*" onChange={onFile} disabled={locked || busy} data-testid="it-proof-file"
              className="block w-full text-xs file:mr-2 file:py-1 file:px-3 file:border-0 file:rounded file:bg-zinc-900 file:text-white" />
          </div>
        </div>
      </Card>

      {(decl.proofs || []).length === 0 ? (
        <Card className="p-6 text-center text-zinc-500 text-sm">No proofs uploaded yet.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Section</TableHead><TableHead>Description</TableHead><TableHead>File</TableHead>
              <TableHead>Size</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(decl.proofs || []).map((p) => (
                <TableRow key={p.id} data-testid={`it-proof-row-${p.id}`}>
                  <TableCell><Badge variant="outline">{p.section}</Badge></TableCell>
                  <TableCell className="text-xs">{p.sub_label || "—"}</TableCell>
                  <TableCell className="text-xs truncate max-w-[200px]" title={p.filename}>{p.filename}</TableCell>
                  <TableCell className="text-xs">{(p.size_bytes / 1024).toFixed(0)} KB</TableCell>
                  <TableCell>
                    {p.verified
                      ? <Badge className="bg-emerald-100 text-emerald-900 text-[10px]"><CheckCircle size={10} className="inline mr-0.5" weight="bold" />Verified</Badge>
                      : <Badge className="bg-zinc-100 text-zinc-700 text-[10px]">Pending</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => view(p.id)}><Eye size={14} /></Button>
                    {!locked && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(p.id)}><TrashSimple size={14} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result || "";
      const idx = String(s).indexOf("base64,");
      resolve(idx >= 0 ? String(s).slice(idx + 7) : String(s));
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/* =========================================================================
   HR — ADMIN DECLARATIONS
   ========================================================================= */
function AdminDeclarations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null); // declaration being inspected

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/income-tax/admin", { params: statusFilter ? { status: statusFilter } : {} });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  if (loading) return <div className="text-zinc-500 text-sm py-6">Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="it-admin-page">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Filed" value={`${data.active_employees - data.not_filed_count}/${data.active_employees}`} testid="it-admin-filed" />
        <StatTile label="Draft" value={data.totals.draft} accent="amber" />
        <StatTile label="Submitted" value={data.totals.submitted} accent="indigo" testid="it-admin-submitted" />
        <StatTile label="Verified" value={data.totals.verified} accent="emerald" />
        <StatTile label="Monthly TDS pool" value={inr(data.estimated_monthly_tds)} accent="emerald" testid="it-admin-tds-pool" />
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-xs">Filter:</Label>
        {["", "submitted", "verified", "draft", "rejected", "locked"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 text-xs rounded border ${statusFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700"}`}
            data-testid={`it-admin-filter-${s || "all"}`}>
            {s ? s[0].toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Regime</TableHead><TableHead>Status</TableHead>
            <TableHead>80C</TableHead><TableHead>HRA rent</TableHead><TableHead>Home Loan</TableHead>
            <TableHead>Monthly TDS</TableHead><TableHead>Annual tax</TableHead><TableHead>Proofs</TableHead><TableHead/>
          </TableRow></TableHeader>
          <TableBody>
            {(data.rows || []).map((r) => {
              const c = r.computation || {};
              return (
                <TableRow key={r.id} data-testid={`it-row-${r.employee_code}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{r.employee_name}</div>
                    <div className="text-[10px] text-zinc-500">{r.employee_code}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] uppercase">{r.regime}</Badge></TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs">{inr(r.deduction_80c)}</TableCell>
                  <TableCell className="text-xs">{inr(r.rent_paid_annual)}</TableCell>
                  <TableCell className="text-xs">{inr(r.home_loan_interest)}</TableCell>
                  <TableCell className="text-xs font-semibold">{inr(c.monthly_tds)}</TableCell>
                  <TableCell className="text-xs">{inr(c.total_tax)}</TableCell>
                  <TableCell className="text-xs">{(r.proofs || []).length}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`it-open-${r.employee_code}`}>Review</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {(data.rows || []).length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-zinc-500 py-8">No declarations match this filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {open && <ReviewDialog decl={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: ["bg-amber-100 text-amber-900", "Draft"],
    submitted: ["bg-indigo-100 text-indigo-900", "Submitted"],
    verified: ["bg-emerald-100 text-emerald-900", "Verified"],
    rejected: ["bg-rose-100 text-rose-900", "Rejected"],
    locked: ["bg-zinc-200 text-zinc-700", "Locked"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100", status];
  return <Badge className={`${cls} text-[10px]`}>{label}</Badge>;
}

function ReviewDialog({ decl: declStub, onClose, onChanged }) {
  const [decl, setDecl] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/income-tax/admin/${declStub.id}`); setDecl(r.data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [declStub.id]);

  const act = async (status) => {
    setBusy(true);
    try {
      await api.post(`/income-tax/admin/${declStub.id}/verify`, { status, note: note || null });
      toast.success(status === "verified" ? "Verified" : "Rejected — employee can re-submit");
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const lockIt = async () => {
    if (!window.confirm("Lock this declaration? Employee will no longer be able to edit.")) return;
    setBusy(true);
    try { await api.post(`/income-tax/admin/${declStub.id}/lock`); toast.success("Locked"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const downloadProof = async (pid) => {
    try {
      const r = await api.get(`/income-tax/admin/proofs/${declStub.id}/${pid}`);
      const { url, data_base64, mime, filename } = r.data;
      if (url) { window.open(url, "_blank"); return; }
      if (data_base64) {
        const a = document.createElement("a");
        a.href = `data:${mime};base64,${data_base64}`;
        a.download = filename || "proof";
        a.click();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!decl) return null;
  const c = decl.computation || {};
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {decl.employee_name} <Badge variant="outline">{decl.employee_code}</Badge>
            <StatusBadge status={decl.status} />
            <Badge variant="outline" className="uppercase">{decl.regime}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <StatTile label="Annual tax" value={inr(c.total_tax)} accent="indigo" />
            <StatTile label="Monthly TDS" value={inr(c.monthly_tds)} accent="emerald" />
            <StatTile label="Net taxable" value={inr(c.net_taxable_income)} />
          </div>
          <Card className="p-3">
            <SectionTitle>Declared amounts</SectionTitle>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
              <KV k="Annual rent" v={inr(decl.rent_paid_annual)} />
              <KV k="80C" v={inr(decl.deduction_80c)} />
              <KV k="80CCD(1B) NPS" v={inr(decl.deduction_80ccd1b)} />
              <KV k="80D self" v={inr(decl.deduction_80d_self)} />
              <KV k="80D parents" v={inr(decl.deduction_80d_parents)} />
              <KV k="Home loan int." v={inr(decl.home_loan_interest)} />
              <KV k="80E edu loan" v={inr(decl.deduction_80e)} />
              <KV k="80G donations" v={inr(decl.deduction_80g)} />
              <KV k="Landlord PAN" v={decl.landlord_pan || "—"} />
              <KV k="Lender" v={decl.home_loan_lender || "—"} />
            </div>
          </Card>
          <Card className="p-3">
            <SectionTitle>Proofs ({(decl.proofs || []).length})</SectionTitle>
            {(decl.proofs || []).length === 0
              ? <div className="text-xs text-zinc-500 mt-2 italic">No proofs uploaded.</div>
              : (
                <Table className="mt-2">
                  <TableHeader><TableRow><TableHead>Section</TableHead><TableHead>File</TableHead><TableHead>Description</TableHead><TableHead/></TableRow></TableHeader>
                  <TableBody>
                    {decl.proofs.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell><Badge variant="outline">{p.section}</Badge></TableCell>
                        <TableCell className="text-xs">{p.filename}</TableCell>
                        <TableCell className="text-xs">{p.sub_label || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => downloadProof(p.id)} data-testid={`it-admin-view-${p.id}`}>View</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </Card>
          {decl.status === "submitted" && (
            <Card className="p-3 bg-amber-50 border-amber-200">
              <Label className="text-xs">Verifier note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="it-verify-note" />
            </Card>
          )}
        </div>
        <DialogFooter>
          {decl.status === "submitted" && (
            <>
              <Button variant="outline" className="text-rose-700 border-rose-300" onClick={() => act("rejected")} disabled={busy} data-testid="it-reject">
                <XCircle size={14} className="mr-1" />Reject
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("verified")} disabled={busy} data-testid="it-verify">
                <CheckCircle size={14} className="mr-1" />Verify
              </Button>
            </>
          )}
          {decl.status === "verified" && (
            <Button variant="outline" onClick={lockIt} disabled={busy} data-testid="it-lock"><LockSimple size={14} className="mr-1" />Lock</Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return <><span className="text-zinc-500">{k}:</span><span className="text-zinc-900 font-medium">{v}</span></>;
}
